import ScreenTitle from '../../../../common/components/screenTitle/ScreenTitle';
import Logger from '../../../../common/services/Logger';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import styles from './VehiclesTab.module.scss';
import Loading from '../../../../common/services/Loading';
import { DefaultAssetImage, DEFAULT_PAGINATION_ITEMS_PER_PAGE, MAX_PAGINATION_ITEMS_PER_PAGE, LOGGER_LOG_TYPE } from '../../../../Config';
import InputSearch from 'common/components/inputSearch/InputSearch';
import { VehicleAlarmListItemDto } from 'api/vehicles/models/VehicleDto';
import { AlarmSettingVehicleDto } from 'api/alarms/models/AlarmSettingDto';
import AlarmsService from 'api/alarms/AlarmsService';
import { useParams } from 'react-router-dom';
import { AlarmVehiclesSearchCriteria } from 'api/alarms/models/AlarmsSearchCriteria';
import { useDebouncedCallback } from 'use-debounce/lib';
import AvatarWithText from 'common/components/avatar/AvatarWithText';
import DataTableWithSidebar from 'common/components/dataTable/DataTableWithSidebar';
import { DataTableColumn, OrderData } from 'common/components/dataTable/models';
import useBidirectionalScroll from 'common/hooks/useBidirectionalScroll';
import SimpleToggleSwitch from 'common/components/simpleToggleSwitch/SimpleToggleSwitch';
import ExportListingButton from 'common/components/exportListingButton/ExportListingButton';

type Props = {
    vehiclesSelected: AlarmSettingVehicleDto[];
    onSelectAllVehicles: (ids: AlarmSettingVehicleDto[]) => void
    onSelectVehicle: (ids: string, value: boolean) => void;
    allVehiclesSelected: boolean,
    setAllVehiclesSelected: (value: boolean) => void;
}

const VehiclesTab = ({ vehiclesSelected, onSelectAllVehicles, onSelectVehicle, allVehiclesSelected, setAllVehiclesSelected }: Props) => {
    const { t } = useTranslation();
    const { addToast } = useToasts();

    const { type, alarmId } = useParams<{ type: string, alarmId: string }>();
    const isDetails = type === 'details';

    const [criteria, setCriteria] = useState<AlarmVehiclesSearchCriteria>({
        itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
        page: 1,
        orderColumn: 'v.registration_number',
        orderBy: 'asc',
        hasGps: true,
        alarmId: alarmId,
    });
    const [totalItems, setTotalItems] = useState<number>(0);
    const [vehicles, setVehicles] = useState<VehicleAlarmListItemDto[]>([]);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const isLoadingData = useRef(false);
    const imageCacheKey = useRef(new Date().getTime());
    const exportListingRef = useRef<{ exportExcel: () => Promise<void> } | null>(null);

    const [order, setOrder] = useState<OrderData<VehicleAlarmListItemDto>>({
        field: 'registrationNumber' as keyof VehicleAlarmListItemDto,
        orderColumn: 'v.registration_number',
        isOrderAsc: true,
    });

    const {
        loadedPageRange,
        isLoadingPrevious,
        shouldLoadAdjacentPages,
        setLoadedPageRange,
        setIsLoadingPrevious,
        resetScrollState,
        prepareLoadPrevious,
        adjustScrollAfterPrepend,
        finishRestoration,
        getHasMore,
        getHasMorePrevious,
        getOnLoadMore,
        getOnLoadPrevious,
    } = useBidirectionalScroll({
        cacheKey: 'ALARMVEHICLESFILTER',
        itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
    });

    useEffect(() => {
        getData(criteria.page > 1);
    }, [criteria]);

    const getData = async (isLoadMore = false) => {
        if (isLoadingData.current) return;
        isLoadingData.current = true;

        try {
            if (!isLoadMore) {
                Loading.show();
            } else {
                setIsLoadingMore(true);
            }

            const result = await AlarmsService.getVehiclesList(criteria);
            setTotalItems(result.totalItems);
            setAllVehiclesSelected(result.totalItems === vehiclesSelected.length);

            const updatedItems = result.items.map(x => {
                const isSelected = vehiclesSelected?.find(sv => sv.vehicleId === x.trackingDeviceId) != null;
                return { ...x, active: isSelected };
            });

            if (isLoadMore) {
                setVehicles(prev => [...prev, ...updatedItems]);
                setLoadedPageRange(prev => ({ ...prev, max: criteria.page }));
            } else {
                setVehicles(updatedItems);
            }

            // Handle bidirectional scroll restoration
            if (shouldLoadAdjacentPages.current) {
                shouldLoadAdjacentPages.current = false;
                const currentPage = criteria.page;
                const totalPages = Math.ceil(result.totalItems / DEFAULT_PAGINATION_ITEMS_PER_PAGE);

                if (currentPage > 1) {
                    try {
                        const prevCriteria = { ...criteria, page: currentPage - 1 };
                        const prevResult = await AlarmsService.getVehiclesList(prevCriteria);
                        const prevItems = prevResult.items.map(x => ({
                            ...x,
                            active: vehiclesSelected?.find(sv => sv.vehicleId === x.trackingDeviceId) != null
                        }));
                        setVehicles(prev => [...prevItems, ...prev]);
                        setLoadedPageRange(prev => ({ ...prev, min: currentPage - 1 }));
                    } catch (e) {
                        Logger.error(LOGGER_LOG_TYPE.ALARMS, `Couldn't load previous page`, e);
                    }
                }

                if (currentPage < totalPages) {
                    try {
                        const nextCriteria = { ...criteria, page: currentPage + 1 };
                        const nextResult = await AlarmsService.getVehiclesList(nextCriteria);
                        const nextItems = nextResult.items.map(x => ({
                            ...x,
                            active: vehiclesSelected?.find(sv => sv.vehicleId === x.trackingDeviceId) != null
                        }));
                        setVehicles(prev => [...prev, ...nextItems]);
                        setLoadedPageRange(prev => ({ ...prev, max: currentPage + 1 }));
                    } catch (e) {
                        Logger.error(LOGGER_LOG_TYPE.ALARMS, `Couldn't load next page`, e);
                    }
                }

                finishRestoration();
            }
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.ALARMS, `Couldn't get information to create alarm`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
            resetScrollState();
        } finally {
            Loading.hide();
            setIsLoadingMore(false);
            isLoadingData.current = false;
        }
    };

    // Update active state when vehiclesSelected changes externally (e.g., select all)
    useEffect(() => {
        setVehicles(prev => prev.map(x => ({
            ...x,
            active: vehiclesSelected?.find(sv => sv.vehicleId === x.trackingDeviceId) != null
        })));
    }, [vehiclesSelected]);

    const onCheckVehicle = (vehicleId: string, isToAdd?: boolean) => {
        onSelectVehicle(vehicleId, isToAdd ?? false);
        setAllVehiclesSelected(totalItems === vehiclesSelected.length + (isToAdd ? 1 : -1));
    };

    const onAllSelectCheck = async (value: boolean) => {
        setAllVehiclesSelected(value);
        if (value) {
            const criteriaAux = { ...criteria, page: 1, itemsPerPage: Number.MAX_SAFE_INTEGER };
            const result = await AlarmsService.getVehiclesList(criteriaAux);
            onSelectAllVehicles(result.items.map(x => ({ vehicleId: x.trackingDeviceId })));
        } else {
            onSelectAllVehicles([]);
        }
    };

    const loadMore = useCallback(() => {
        if (isLoadingData.current || isLoadingMore || vehicles.length >= totalItems) return;
        const nextPage = loadedPageRange.max + 1;
        setCriteria(prev => ({ ...prev, page: nextPage }));
    }, [isLoadingMore, vehicles.length, totalItems, loadedPageRange.max]);

    const loadPrevious = useCallback(async () => {
        if (isLoadingPrevious || loadedPageRange.min <= 1) return;

        setIsLoadingPrevious(true);
        const prevPage = loadedPageRange.min - 1;

        try {
            prepareLoadPrevious();
            const prevCriteria: AlarmVehiclesSearchCriteria = {
                ...criteria,
                page: prevPage,
                itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
            };
            const result = await AlarmsService.getVehiclesList(prevCriteria);
            const prevItems = result.items.map(x => ({
                ...x,
                active: vehiclesSelected?.find(sv => sv.vehicleId === x.trackingDeviceId) != null
            }));

            setVehicles(prev => [...prevItems, ...prev]);
            setLoadedPageRange(prev => ({ ...prev, min: prevPage }));
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.ALARMS, `Couldn't get previous vehicles page`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
        } finally {
            setIsLoadingPrevious(false);
        }
    }, [isLoadingPrevious, loadedPageRange.min, criteria, vehiclesSelected, addToast, t]);

    useEffect(() => {
        if (!isLoadingPrevious) {
            adjustScrollAfterPrepend();
        }
    }, [vehicles, isLoadingPrevious, adjustScrollAfterPrepend]);

    const handleOrderChange = (orderData: OrderData<VehicleAlarmListItemDto>) => {
        setOrder(orderData);
        setVehicles([]);
        resetScrollState();
        setTotalItems(0);
        setCriteria(prev => ({
            ...prev,
            page: 1,
            orderBy: orderData.isOrderAsc ? 'asc' : 'desc',
            orderColumn: orderData.orderColumn || orderData.field as string,
        }));
    };

    const debounced = useDebouncedCallback((value: string) => {
        if (value === criteria.allIn) return;
        setVehicles([]);
        resetScrollState();
        setTotalItems(0);
        setCriteria(prev => ({ ...prev, allIn: value, page: 1 }));
    }, 500);

    const dataTableColumns: DataTableColumn<VehicleAlarmListItemDto>[] = useMemo(() => {
        const cols: DataTableColumn<VehicleAlarmListItemDto>[] = [
            {
                field: 'photoUrl' as keyof VehicleAlarmListItemDto,
                name: t('common.image') as string,
                width: 80,
                hideOrderButton: true,
                excludeFromExport: true,
                renderCell: row => (
                    <div className={styles.contentImage} style={{ width: '5rem', height: '3rem' }}>
                        <div className={styles.img} style={{
                            width: '5rem', height: '3rem',
                            backgroundImage: row?.photoUrl
                                ? 'url(' + row.photoUrl + '?_=' + imageCacheKey.current + ')'
                                : row?.vehicleSegmentDefaultPhotoUrl
                                    ? 'url(' + row.vehicleSegmentDefaultPhotoUrl + '?_=' + imageCacheKey.current + ')'
                                    : 'url(' + DefaultAssetImage + ')'
                        }} />
                    </div>
                ),
            },
            {
                field: 'registrationNumber' as keyof VehicleAlarmListItemDto,
                name: t('alarms.vehicles_tab.registration') as string,
                orderColumn: 'v.registration_number',
                width: 140,
                renderCell: row => <><span className={styles.columnPlate}>{row.registrationNumber}</span></>,
            },
            {
                field: 'vehicleBrandName' as keyof VehicleAlarmListItemDto,
                name: t('alarms.vehicles_tab.brand') as string,
                orderColumn: 'vb.name',
                width: 140,
                renderCell: row => <>{row.vehicleBrandName || '-'}</>,
            },
            {
                field: 'vehicleModelName' as keyof VehicleAlarmListItemDto,
                name: t('alarms.vehicles_tab.model') as string,
                orderColumn: 'vm.name',
                width: 140,
                renderCell: row => <>{row.vehicleModelName || '-'}</>,
            },
            {
                field: 'driverName' as keyof VehicleAlarmListItemDto,
                name: t('alarms.vehicles_tab.responsible') as string,
                orderColumn: 'driverName',
                width: 180,
                hideOn: ['sm'],
                exportValue: row => row.driverName ?? '',
                renderCell: row => (
                    <AvatarWithText src={row.driverPhotoUrl}
                        size="x2"
                        hideAvatar={!row.driverName}><div className={styles.bold}>{row.driverName || '-'}</div></AvatarWithText>
                ),
            },
            {
                field: 'notificationsTotal' as keyof VehicleAlarmListItemDto,
                name: t('alarms.vehicles_tab.notifications') as string,
                width: 120,
                hideOrderButton: true,
                renderCell: row => <>{row.notificationsTotal}</>,
            },
            {
                field: 'active' as keyof VehicleAlarmListItemDto,
                name: t('alarms.vehicles_tab.active') as string,
                width: 80,
                hideOrderButton: true,
                preventClick: true,
                exportValue: row => t(`common.${row.active ? 'yes' : 'no'}` as any) as string,
                renderCell: row => (
                    <SimpleToggleSwitch
                        checked={row.active ?? false}
                        disabled={isDetails}
                        onChange={((val: boolean) => onCheckVehicle(row.trackingDeviceId, val)) as any}
                    />
                ),
            },
        ];
        return cols;
    }, [t, isDetails, vehiclesSelected]);

    const fetchAllForExport = useCallback(async () => {
        const result = await AlarmsService.getVehiclesList({
            ...criteria,
            page: 1,
            itemsPerPage: MAX_PAGINATION_ITEMS_PER_PAGE,
        });
        return result.items.map(x => ({
            ...x,
            active: vehiclesSelected?.find(sv => sv.vehicleId === x.trackingDeviceId) != null
        }));
    }, [criteria, vehiclesSelected]);

    return (
        <ScreenTitle title={t('alarms.vehicles_tab.title')}>
            <div className={styles.tabToolbar}>
                <div className={styles.toolbarRow}>
                    <InputSearch
                        onChangeValue={debounced}
                        defaultValue={criteria.allIn}
                        placeholder={t('alarms.vehicles_tab.search')}
                        onKeyDown={(e: any) => {
                            if (e.keyCode === 13) {
                                e.preventDefault();
                            }
                        }}
                    />
                    <ExportListingButton exportRef={exportListingRef} />
                </div>
                <div className={styles.flexRow}>
                    <SimpleToggleSwitch
                        checked={allVehiclesSelected}
                        disabled={isDetails}
                        onChange={(() => onAllSelectCheck(!allVehiclesSelected)) as any}
                    />
                    <div>{t('alarms.vehicles_tab.select_all_vehicles')}</div>
                </div>
            </div>
            <DataTableWithSidebar
                cacheKey="ALARM_VEHICLES_TABLE"
                columns={dataTableColumns}
                rows={vehicles}
                defaultOrder={order}
                onOrderChange={handleOrderChange}
                onRowId={row => row.trackingDeviceId}
                infiniteScroll={{
                    isLoading: isLoadingMore,
                    hasMore: getHasMore(vehicles.length, totalItems),
                    onLoadMore: getOnLoadMore(loadMore),
                    hasMorePrevious: getHasMorePrevious(),
                    isLoadingPrevious,
                    onLoadPrevious: getOnLoadPrevious(loadPrevious),
                }}
                exportExcel={{
                    fetchAll: fetchAllForExport,
                    fileName: t('vehicles.title') as string,
                }}
                exportRef={exportListingRef}
                hideExportInSidebar={true}
            />
        </ScreenTitle>
    );
};

export default VehiclesTab;
